import { ArrowLeftIcon, UploadIcon, CameraIcon, ClipboardCopyIcon } from '@heroicons/react/outline'
import PHP from 'assets/currencies/php.svg'
import ReceiverQRCode from 'assets/receiver-qr-code.png'
import Card from 'components/Card'
import PayLayout from 'components/Layouts/PayLayout'
import Loader from 'components/Loader'
import useAuth from 'hooks/useAuth'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { shortenAddress } from 'utils/address'

enum PayWithQrStateEnum {
  Loading,
  QRSelection,
  PaymentDetails,
}

const PayWithQr = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const [pageRender, setPageRender] = useState(PayWithQrStateEnum.QRSelection)
  const [paymentDetails, setPaymentDetails] = useState({
    nameOfBank: 'Unionbank',
    accountName: 'Juan dela Cruz',
    accountNumber: '**** **** 1111',
    walletAddress: '',
  })
  const [copied, setCopied] = useState(false)

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }

  const handleUploadQR = async () => {
    setPageRender(PayWithQrStateEnum.Loading)
    try {
      const res = await fetch(
        'https://sc.identity-dev.ubx.ph/stablecoin-app/wallets/23Cf6D1Ed98C1DC01231F47b50b9402FF15C60cB',
      )
      const _res = await res.json()
      setPaymentDetails({
        ...paymentDetails,
        walletAddress: _res.data.address,
      })
      setPageRender(PayWithQrStateEnum.PaymentDetails)
      auth.setRecipientAddress(_res.data.address)
    } catch (error) {
      setPageRender(PayWithQrStateEnum.QRSelection)
    }
  }

  const handlePayNow = () => {
    auth.setRecipientAddress(paymentDetails.walletAddress)
    navigate('/payment/enter-amount')
  }

  return (
    <PayLayout>
      <Card>
        <div className="p-6 border-b border-neutral-200">
          <h2 className="text-xl font-semibold text-neutral-800">Pay with QR</h2>
          <p className="text-sm text-neutral-500 mt-1">
            {pageRender === PayWithQrStateEnum.PaymentDetails
              ? 'Confirm payment details'
              : 'Scan or upload a QR code to pay'}
          </p>
        </div>

        <div className="p-6">
          {pageRender === PayWithQrStateEnum.Loading && (
            <div className="flex items-center justify-center py-12">
              <Loader size="lg" />
            </div>
          )}

          {pageRender === PayWithQrStateEnum.QRSelection && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="uploadQR"
                  className="card card-hover p-8 text-center cursor-pointer block group"
                >
                  <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <UploadIcon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="font-semibold text-neutral-800 mb-1">Upload QR</h3>
                  <p className="text-sm text-neutral-500">Choose from gallery</p>
                </label>
                <input
                  id="uploadQR"
                  accept="image/*"
                  type="file"
                  multiple={false}
                  className="hidden"
                  onChange={handleUploadQR}
                />
              </div>

              <div className="card p-8 text-center opacity-50 cursor-not-allowed">
                <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                  <CameraIcon className="w-8 h-8 text-neutral-400" />
                </div>
                <h3 className="font-semibold text-neutral-800 mb-1">Scan QR</h3>
                <p className="text-sm text-neutral-500">Coming soon</p>
              </div>
            </div>
          )}

          {pageRender === PayWithQrStateEnum.PaymentDetails && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex-1 space-y-4">
                  <div className="bg-primary-50 rounded-xl p-4">
                    <p className="text-sm text-primary-700">
                      <strong>{paymentDetails.accountName}</strong> is requesting payment
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-4">
                      <img className="w-6 h-6 rounded-full" src={PHP} alt="PHP" />
                      <span className="font-medium text-neutral-800">PHP Account</span>
                    </div>

                    {[
                      { label: 'Bank', value: paymentDetails.nameOfBank },
                      { label: 'Account Name', value: paymentDetails.accountName },
                      { label: 'Account Number', value: paymentDetails.accountNumber },
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between py-2 border-b border-neutral-100">
                        <span className="text-neutral-500 text-sm">{item.label}</span>
                        <span className="font-medium text-neutral-800 text-sm">{item.value}</span>
                      </div>
                    ))}

                    <div className="flex justify-between items-center py-2">
                      <span className="text-neutral-500 text-sm">Wallet</span>
                      <button
                        className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                        onClick={() => handleCopy(paymentDetails.walletAddress)}
                      >
                        {shortenAddress(paymentDetails.walletAddress)}
                        <ClipboardCopyIcon className="w-4 h-4" />
                        {copied && <span className="text-xs text-success-600">Copied!</span>}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex-shrink-0 flex justify-center">
                  <div className="bg-white rounded-xl border border-neutral-200 p-2">
                    <img className="w-32 h-32" src={ReceiverQRCode} alt="QR Code" />
                  </div>
                </div>
              </div>

              <button onClick={handlePayNow} className="btn-primary btn-lg w-full">
                Continue to Payment
              </button>

              <button
                onClick={() => setPageRender(PayWithQrStateEnum.QRSelection)}
                className="flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-800 transition-colors mx-auto"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back
              </button>
            </div>
          )}
        </div>
      </Card>
    </PayLayout>
  )
}

export default PayWithQr
